import { cfgSupportChatApiUrl } from '@infinitro/config';
import { getCurrentToken, getValue, storeValue } from '@infinitro/global';
import { getEarlyFingerPrint, getLateFingerPrint, stateFingerprint } from '@infinitro/visit';
import { get } from 'svelte/store';
import { supportChatDebug, supportChatWarn } from '../debug';
const SUPPORT_CHAT_GUEST_DEVICE_ID_KEY = 'support_chat_guest_device_id';
const SUPPORT_CHAT_DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
let currentSupportChatActor;
export function getCurrentSupportChatActor() {
    return currentSupportChatActor;
}
export function normalizeSupportChatBaseUrl(raw) {
    return raw.replace(/\/+$/, '');
}
function supportChatApiPath(path, actorKind) {
    const p = path.startsWith('/') ? path : `/${path}`;
    if (p.startsWith('/player/') || p === '/player' || p.startsWith('/guest/') || p === '/guest') {
        return p;
    }
    return actorKind ? `/${actorKind}${p}` : p;
}
export function supportChatHttpUrl(path, actorKind) {
    const base = normalizeSupportChatBaseUrl(cfgSupportChatUrlOrThrow());
    const p = supportChatApiPath(path, actorKind);
    return `${base}${p}`;
}
export function supportChatWsUrl(actor) {
    const base = cfgSupportChatApiUrl() ?? '';
    if (!base)
        throw new Error('support chat API URL is not configured');
    const u = new URL(base.includes('://') ? base : `https://${base}`);
    const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:';
    const pathPrefix = u.pathname.replace(/\/+$/, '');
    if (actor.kind === 'player') {
        return `${wsProto}//${u.host}${pathPrefix}/player/ws?token=${encodeURIComponent(actor.token)}`;
    }
    return `${wsProto}//${u.host}${pathPrefix}/guest/ws?device_id=${encodeURIComponent(actor.deviceId)}`;
}
function cfgSupportChatUrlOrThrow() {
    const u = cfgSupportChatApiUrl();
    if (!u)
        throw new Error('support chat API URL is not configured');
    return u;
}
function isValidGuestDeviceId(value) {
    return typeof value === 'string' && SUPPORT_CHAT_DEVICE_ID_PATTERN.test(value);
}
function createFallbackGuestDeviceId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    const randomPart = Math.random().toString(36).slice(2, 12);
    return `guest-${Date.now().toString(36)}-${randomPart}`;
}
async function resolveGuestDeviceId() {
    const storedFingerprint = get(stateFingerprint);
    if (isValidGuestDeviceId(storedFingerprint))
        return storedFingerprint;
    const earlyFingerprint = await getEarlyFingerPrint();
    if (isValidGuestDeviceId(earlyFingerprint))
        return earlyFingerprint;
    const lateFingerprint = await getLateFingerPrint();
    if (isValidGuestDeviceId(lateFingerprint))
        return lateFingerprint;
    const storedFallback = await getValue(SUPPORT_CHAT_GUEST_DEVICE_ID_KEY);
    if (isValidGuestDeviceId(storedFallback))
        return storedFallback;
    const fallback = createFallbackGuestDeviceId();
    if (!isValidGuestDeviceId(fallback))
        return undefined;
    await storeValue(SUPPORT_CHAT_GUEST_DEVICE_ID_KEY, fallback);
    return fallback;
}
export async function getSupportChatActor() {
    const token = getCurrentToken();
    if (token) {
        currentSupportChatActor = { kind: 'player', token };
        return currentSupportChatActor;
    }
    const deviceId = await resolveGuestDeviceId();
    if (!deviceId) {
        currentSupportChatActor = undefined;
        return undefined;
    }
    currentSupportChatActor = { kind: 'guest', deviceId };
    return currentSupportChatActor;
}
function applySupportChatAuthHeaders(headers, actor) {
    if (actor.kind === 'player') {
        headers.set('x-access-token', actor.token);
        return;
    }
    headers.set('x-guest-device-id', actor.deviceId);
}
function supportChatLanguageId(browserLocale) {
    return browserLocale.split('-')[0]?.trim().toLowerCase() ?? '';
}
export function supportChatClientContextHeaders(browserLocale) {
    const now = new Date();
    const headers = {
        'x-local-time': now.toISOString(),
        'x-time-zone-offset': String(now.getTimezoneOffset())
    };
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone)
        headers['x-time-zone'] = timeZone;
    const loc = browserLocale?.trim() ||
        (typeof navigator !== 'undefined' && typeof navigator.language === 'string'
            ? navigator.language.trim()
            : '');
    if (loc) {
        headers['x-locale'] = loc;
        const languageId = supportChatLanguageId(loc);
        if (languageId)
            headers['x-language-id'] = languageId;
    }
    return headers;
}
async function sendSupportChatFetch(actor, path, init) {
    const url = supportChatHttpUrl(path, actor.kind);
    const headers = new Headers(init.headers);
    applySupportChatAuthHeaders(headers, actor);
    if (!headers.has('Accept'))
        headers.set('Accept', 'application/json');
    let body = init.body;
    if (init.json !== undefined) {
        headers.set('Content-Type', 'application/json');
        body = JSON.stringify(init.json);
    }
    const method = init.method ?? 'GET';
    supportChatDebug('HTTP request start', {
        method,
        url,
        actorKind: actor.kind,
        hasToken: actor.kind === 'player',
        hasGuestDeviceId: actor.kind === 'guest',
        body: init.json ?? body ?? null
    });
    const startedAt = Date.now();
    const res = await fetch(url, { ...init, headers, body });
    const elapsedMs = Date.now() - startedAt;
    const log = res.ok ? supportChatDebug : supportChatWarn;
    log('HTTP request complete', {
        method,
        url,
        status: res.status,
        ok: res.ok,
        elapsedMs
    });
    return res;
}
export async function refreshGuestSupportChatSession(deviceId) {
    const resolvedDeviceId = deviceId ?? (await resolveGuestDeviceId());
    if (!resolvedDeviceId)
        throw new Error('support chat guest device id is not available');
    const actor = { kind: 'guest', deviceId: resolvedDeviceId };
    const res = await sendSupportChatFetch(actor, '/session', { method: 'POST' });
    if (!res.ok) {
        supportChatWarn('Guest session refresh failed', { status: res.status });
        throw new Error(`Support chat guest session HTTP ${res.status}`);
    }
}
export async function supportChatFetch(path, init = {}) {
    const actor = await getSupportChatActor();
    if (!actor)
        throw new Error('support chat identity is not available');
    const res = await sendSupportChatFetch(actor, path, init);
    if (actor.kind !== 'guest' || res.status !== 401 || supportChatApiPath(path) === '/session') {
        return res;
    }
    supportChatWarn('Guest request unauthorized; refreshing session and retrying once', { path });
    await refreshGuestSupportChatSession(actor.deviceId);
    return sendSupportChatFetch(actor, path, init);
}
